<template>
  <div class="modern-home">
    <!-- ✨ Hero Slider with Desktop/Mobile Images -->
    <div v-if="banners.length > 0" id="heroCarousel" class="carousel slide carousel-fade" data-bs-ride="carousel" data-bs-interval="5000">
      <div class="carousel-indicators">
        <button
          v-for="(banner, index) in banners"
          :key="'indicator-' + banner._id"
          type="button"
          data-bs-target="#heroCarousel"
          :data-bs-slide-to="index"
          :class="{ active: index === 0 }"
          :aria-current="index === 0 ? 'true' : 'false'"
          :aria-label="'Slide ' + (index + 1)"
        ></button>
      </div>
      
      <div class="carousel-inner">
        <div
          v-for="(banner, index) in banners"
          :key="banner._id"
          :class="['carousel-item', 'hero-slide-modern', { active: index === 0 }]"
        >
          <!-- RESPONSIVE IMAGE: Different images for mobile vs desktop -->
          
          <picture>
            <source 
              media="(max-width: 768px)" 
              :srcset="banner.bannerImageUrlMobile || banner.bannerImageUrl"
            >
            <img 
              :src="getResponsiveImage(banner)"
              :alt="banner.title || 'Banner'"
              class="hero-slide-img"
            />
          </picture>
          
          <div class="hero-overlay-modern">
            <div class="hero-content animate-fade-in">
              <h1 v-if="banner.title" class="hero-title">{{ banner.title }}</h1>
              <p v-if="banner.description" class="hero-description">{{ banner.description }}</p>
              <a
                v-if="banner.redirectLink"
                :href="banner.redirectLink"
                class="btn-hero-cta"
              >
                {{ banner.buttonText || 'Explore Now' }}
                <i class="fas fa-arrow-right"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      <button class="carousel-control-prev" type="button" data-bs-target="#heroCarousel" data-bs-slide="prev">
        <span class="carousel-control-prev-icon" aria-hidden="true"></span>
        <span class="visually-hidden">Previous</span>
      </button>
      <button class="carousel-control-next" type="button" data-bs-target="#heroCarousel" data-bs-slide="next">
        <span class="carousel-control-next-icon" aria-hidden="true"></span>
        <span class="visually-hidden">Next</span>
      </button>
    </div>

    <!-- ✨ Categories Section with Hover Effects -->
    <section v-if="categories.length > 0" class="categories-modern">
      <div class="container">
        <div class="section-header" data-aos="fade-down" data-aos-duration="800">
          <h2 class="section-title-modern">Shop by Category</h2>
          <div class="title-underline"></div>
        </div>
        
        <div class="row g-4">
          <div
              class="col-6 col-md-4 col-lg-3"
              v-for="(cat, index) in categories"
              :key="cat._id"
              data-aos="zoom-in"
              :data-aos-delay="index * 100"
              data-aos-duration="600"
            >
            <a :href="`/shop/${cat.slug}`" class="category-card-modern">
              <div class="category-image-wrapper">
                <img
                  :src="cat.categoryImageUrl"
                  :alt="cat.title"
                  class="category-image"
                />
                <div class="category-overlay">
                  <i class="fas fa-arrow-right"></i>
                </div>
              </div>
              <h5 class="category-title">{{ cat.title }}</h5>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- ✨ Products Section with Grid Animation -->
    <section v-if="products.length > 0" class="products-modern">
      <div class="container">
        <div class="section-header" data-aos="fade-down" data-aos-duration="800">
        <h2 class="section-title-modern">Our Best Products</h2>
        <div class="title-underline"></div>
      </div>
        
        <div class="row g-4">
          <div
            class="col-6 col-md-4 col-lg-3"
            v-for="(product, index) in products"
            :key="product._id"
            data-aos="flip-up"
            :data-aos-delay="index * 50"
            data-aos-duration="600"
          >
            <div class="product-card-modern">
              <!-- Offer Badge -->
              <div v-if="product.offerPrice" class="offer-badge">
                <span>{{ product.offerPriceText || 'Sale' }}</span>
              </div>

              <!-- Product Image -->
              <a href="javascript:void(0)" @click="$router.push(`/product/${product.slug}`)" class="product-image-link">
                <div class="product-image-wrapper">
                  <img
                    :src="product.mainImageUrl"
                    :alt="product.name"
                    class="product-image"
                  />
                  <div class="product-overlay">
                    <button class="btn-quick-view">
                      <i class="fas fa-eye"></i>
                    </button>
                  </div>
                </div>
              </a>

              <!-- Product Details -->
              <div class="product-details">
                <h6 class="product-name">{{ product.name }}</h6>

                <!-- ✨ IMPROVED Price Display -->
                <div class="product-price-block" :class="{ 'has-offer': product.offerPrice }">
                  <!-- When offer price exists -->
                  <template v-if="product.offerPrice">
                    <div class="price-row">
                      <span class="price-sale-current">₹{{ product.offerPrice }}</span>
                      <span class="price-was-label">was</span>
                      <span class="price-original-strike">₹{{ product.price }}</span>
                      <span class="price-savings-pill" v-if="product.price > product.offerPrice">
                        Save ₹{{ product.price - product.offerPrice }}
                      </span>
                    </div>
                  </template>
                  <!-- When no offer price -->
                  <template v-else>
                    <div class="price-row">
                      <span class="price-regular">₹{{ product.price }}</span>
                    </div>
                  </template>
                </div>

                <!--<button @click="addProductToCart(product, true)" class="btn-add-cart">
                  <i class="fas fa-shopping-bag"></i>
                  <span>Buy Now</span>
                </button>-->
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ✨ Workshops Section with Card Animation -->
    <section v-if="workshops.length > 0" class="workshops-modern">
      <div class="container">
        <div class="section-header" data-aos="slide-right" data-aos-duration="800">
          <span class="section-subtitle">Learn & Create</span>
          <h2 class="section-title-modern">Upcoming Workshops</h2>
          <div class="title-underline"></div>
        </div>
        
        <div class="row g-4">
          <div
              class="col-md-6"
              v-for="(workshop, index) in workshops"
              :key="workshop._id"
              data-aos="slide-left"
              :data-aos-delay="index * 150"
              data-aos-duration="700"
            >
            <div class="workshop-card-modern">
              <div class="workshop-icon">
                <i class="fas fa-palette"></i>
              </div>
              <div class="workshop-content">
                <h5 class="workshop-title text-start">{{ workshop.title }}</h5>
                <div class="workshop-meta text-start">
                  <div class="meta-item">
                    <i class="fas fa-calendar-alt"></i>
                    <span>{{ workshop.date }}</span>
                  </div>
                  <div class="meta-item">
                    <i class="fas fa-clock"></i>
                    <span>{{ workshop.time }}</span>
                  </div>
                  <div class="meta-item">
                    <i class="fas fa-map-marker-alt"></i>
                    <span>{{ workshop.address }}</span>
                  </div>
                </div>
                <div class="workshop-footer">
                  <span class="workshop-price">₹{{ workshop.price }}</span>
                  <div class="workshop-cta-buttons">
                    <a href="javascript:void(0)" @click="registerOnWhatsapp(workshop)" class="btn-register-whatsapp">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="vertical-align:-2px;"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.549 4.107 1.51 5.833L.057 23.535a.75.75 0 0 0 .916.927l5.834-1.53A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.708 9.708 0 0 1-4.95-1.355l-.355-.211-3.666.962.978-3.58-.231-.368A9.715 9.715 0 0 1 2.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z"/></svg>
                      Register via WhatsApp
                    </a>
                    <a href="javascript:void(0)" @click="openRegistrationModal(workshop)" class="btn-register">
                      Register Now
                      <i class="fas fa-arrow-right" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ✨ Blog Section -->
    <!--<section class="blog-modern">
      <div class="container">
        <div class="section-header" data-aos="fade-up" data-aos-duration="800">
          <span class="section-subtitle">Stories</span>
          <h2 class="section-title-modern">From Our Blog</h2>
          <div class="title-underline"></div>
        </div>
        
        <div class="row g-4">
          <div
            class="col-md-4"
            v-for="i in 3"
            :key="i"
            data-aos="flip-left"
            :data-aos-delay="i * 100"
          >
            <div class="blog-card-modern">
              <div class="blog-image-wrapper">
                <img src="images/blogs/blog1.jpg" alt="Blog" class="blog-image" />
                <div class="blog-date">
                  <span class="date-day">15</span>
                  <span class="date-month">Jan</span>
                </div>
              </div>
              <div class="blog-content">
                <h5 class="blog-title">How Handmade Gifts Create Memories</h5>
                <p class="blog-excerpt">Discover the joy and meaning behind handcrafted presents...</p>
                <a href="#" class="btn-read-more">
                  Read More
                  <i class="fas fa-arrow-right"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
        
        <div class="text-center mt-5" data-aos="fade-up" data-aos-delay="300" data-aos-duration="600">
        <a href="#" class="btn-view-all">
            View All Articles
            <i class="fas fa-arrow-right"></i>
          </a>
        </div>
      </div>
    </section>-->
  
    <!-- Registration Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="closeModal">
          <i class="fas fa-times"></i>
        </button>
        
        <div class="modal-header">
          <h3>Register for <b>{{ selectedWorkshop?.title }}</b></h3>
        </div>
        
        <form @submit.prevent="submitRegistration" class="registration-form text-start">
          <div class="form-group">
            <label class="form-label">Full Name *</label>
            <input 
              type="text" 
              v-model="formData.name" 
              class="form-control" 
              placeholder="Your name"
              required
            >
          </div>
          
          <div class="form-group">
            <label class="form-label">Phone Number *</label>
            <input 
              type="tel" 
              v-model="formData.phone" 
              class="form-control" 
              placeholder="+91 98765 43210"
              required
            >
          </div>
          
          <div class="form-group">
            <label class="form-label">Number of Participants *</label>
            <select v-model="formData.participants" class="form-control" required>
              <option value="1">1 Person</option>
              <option value="2">2 People</option>
              <option value="3">3 People</option>
              <option value="4">4 People</option>
            </select>
          </div>
          
          <div class="form-group">
            <label class="form-label">Special Requirements (Optional)</label>
            <textarea 
              v-model="formData.requirements" 
              class="form-control" 
              rows="3"
              placeholder="Any dietary restrictions, accessibility needs, etc."
            ></textarea>
          </div>
          
          <div class="modal-footer-custom">
            <div class="total-price">
              <span>Total:</span>
              <strong>₹{{ calculateTotal() }}</strong>
            </div>
            <button type="submit" class="btn-submit" :disabled="isSubmitting">
              <span v-if="!isSubmitting">
                <i class="fas fa-check-circle"></i>
                Confirm Registration
              </span>
              <span v-else>
                <i class="fas fa-spinner fa-spin"></i>
                Processing...
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import {useToast} from 'vue-toast-notification';
const $toast = useToast();
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';
import BannerService from "../services/BannerService";
import ProductService from "../services/ProductService";
import { EventBus } from '@/eventBus'

export default {
  name: 'HomeView',
  components: {
    VueTelInput
  },
  data() {
    return {
      isDisabled: false,
      banners: [],
      categories: [],
      products: [],
      workshops: [],
      // ADD inside data() return, after workshops: []
      showModal: false,
      selectedWorkshop: null,
      WHATSAPP_NUMBER: "919512981118", // 🔁 Replace with your number
      isSubmitting: false,
      formData: {
        name: '',
        phone: '',
        participants: '1',
        requirements: ''
      },
    };
  },
  methods: {
    getBanners() {
      BannerService.getBanners()
        .then((response) => {
            this.banners = response.data.data;
            console.log('Banners loaded:', this.banners);
            
            // Initialize carousel after banners load
            this.$nextTick(() => {
              this.initializeCarousel();
            });
        }).catch((e) => {
            this.$toast.error(e.response?.data?.respones_msg || 'Failed to load banners');
        });
    },
    registerOnWhatsapp(workshop) {
      const message =
        `Hi Bee’s Atelier! 🌼\n\n` +
        `I came across your workshop and would love to join ✨\n\n` +
        `🎨 Workshop: ${workshop.title}\n` +
        `📅 Date: ${workshop.date}\n` +
        `⏰ Time: ${workshop.time}\n` +
        `📍 Venue: ${workshop.address}\n` +
        `💰 Price: ₹${workshop.price}\n\n` +
        `It looks so exciting! 💛\n` +
        `Please let me know the next steps to confirm my booking.`;

      window.open(
        `https://wa.me/${this.WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`,
        '_blank'
      );
    },
    
    initializeCarousel() {
      if (typeof window.bootstrap !== 'undefined' && this.banners.length > 0) {
        const carouselElement = document.querySelector('#heroCarousel');
        if (carouselElement) {
          const existingCarousel = window.bootstrap.Carousel.getInstance(carouselElement);
          if (existingCarousel) {
            existingCarousel.dispose();
          }
          
          new window.bootstrap.Carousel(carouselElement, {
            interval: 5000,
            ride: 'carousel',
            wrap: true,
            keyboard: true,
            pause: 'hover',
            touch: true
          });
          
          console.log('✅ Carousel initialized');
        }
      }
    },
    
    getHomePageData() {
      ProductService.getHomePageData()
        .then((response) => {
            this.categories = response.data.data.categories;
            this.products = response.data.data.products;
            this.workshops = response.data.data.workshops;
        }).catch((e) => {
            this.$toast.error(e.response?.data?.respones_msg || 'Failed to load data');
        });
    },
    getResponsiveImage(banner) {
      if (window.innerWidth <= 768 && banner.bannerImageUrlMobile) {
        return banner.bannerImageUrlMobile;
      }
      return banner.bannerImageUrl;
    },
    
    async addProductToCart(product, redirectToCartPage = false) {
      let data = {
        productId: product._id,
        productDetail: product,
        qty: 1,
      };

      await ProductService.addProductToCart(data)
        .then((response) => {
          EventBus.emit('cart-updated')
          $toast.success(response.data.respones_msg);
          if (redirectToCartPage) {
            this.$router.push({ path: '/cart' });
          }
        })
        .catch((e) => {
          $toast.error(e.response?.data?.respones_msg || 'Failed to add to cart');
        });
    },
    openRegistrationModal(workshop) {
      this.selectedWorkshop = workshop;
      this.showModal = true;
      document.body.style.overflow = 'hidden';
    },

    closeModal() {
      this.showModal = false;
      this.selectedWorkshop = null;
      this.formData = {
        name: '',
        phone: '',
        participants: '1',
        requirements: ''
      };
      document.body.style.overflow = 'auto';
    },

    calculateTotal() {
      if (!this.selectedWorkshop) return 0;
      return this.selectedWorkshop.price * parseInt(this.formData.participants);
    },

    async submitRegistration() {
      this.isSubmitting = true;
      try {
        const orderResponse = await ProductService.submitWorkshopRegistration({
          workshopId: this.selectedWorkshop._id,
          name: this.formData.name,
          phoneNumber: this.formData.phone,
          totalPerson: parseInt(this.formData.participants),
          notes: this.formData.requirements
        });

        const { razorpayOrder, customer } = orderResponse.data.data;

        const options = {
          key: process.env.VUE_APP_RAZORPAY_KEY,
          amount: razorpayOrder.amount,
          currency: razorpayOrder.currency,
          name: "Bee's Atelier",
          description: `Workshop: ${this.selectedWorkshop.title}`,
          order_id: razorpayOrder.id,
          prefill: {
            name: customer.name,
            contact: customer.phone
          },
          theme: { color: "#76ACAF" },
          handler: async (response) => {
            await this.verifyPayment(response, this.selectedWorkshop._id);
            this.closeModal();
          },
          modal: {
            ondismiss: () => {
              this.isSubmitting = false;
              $toast.info('Payment cancelled');
            }
          }
        };

        const rzp = new window.Razorpay(options);

        rzp.on("payment.failed", (response) => {
          $toast.error(response.error.description || 'Payment failed');
          this.isSubmitting = false;
        });

        rzp.open();

      } catch (error) {
        console.log({ error });
        $toast.error('Registration failed. Please try again.');
      } finally {
        this.isSubmitting = false;
      }
    },

    async verifyPayment(razorpayResponse, orderId) {
      try {
        const verifyResponse = await ProductService.verifyWorkshopPayment({
          razorpay_order_id: razorpayResponse.razorpay_order_id,
          razorpay_payment_id: razorpayResponse.razorpay_payment_id,
          razorpay_signature: razorpayResponse.razorpay_signature,
          orderId: orderId
        });

        $toast.success('Payment successful! Your registration is confirmed.');

        this.$router.push({
          name: 'Workshop Payment Success',
          query: {
            workshopId: orderId,
            registrationId: verifyResponse.data.data
          }
        });

      } catch (error) {
        console.error('Payment Verification Error:', error);
        $toast.error('Payment verification failed. Please contact support.');
      }
    },
  },
  mounted() {
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-in-out',
        once: true,
        offset: 100
      });
    }
  },
  beforeMount() {
    document.body.style.overflow = 'auto';
    this.getBanners()
    this.getHomePageData()
  },
}
</script>

<style scoped>
/* Import Modern Fonts */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Playfair+Display:wght@400;500;600;700&display=swap');

/* Global Styles */
.modern-home {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  overflow-x: hidden;
}

/* ========== HERO SECTION ========== */
.hero-slide-modern {
  height: 600px;
  min-height: 500px;
  position: relative;
  overflow: hidden;
  background: #f8f9fa;
}

.hero-slide-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  z-index: 0;
}

.hero-overlay-modern {
  position: absolute;
  inset: 0;
  /*background: linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 100%);*/
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.hero-content {
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 20px;
  position: relative;
  z-index: 2;
}

.animate-fade-in {
  animation: fadeInUp 1s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.hero-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  font-weight: 700;
  margin-bottom: 20px;
  line-height: 1.2;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.hero-description {
  font-size: clamp(1rem, 2vw, 1.3rem);
  margin-bottom: 35px;
  line-height: 1.6;
  font-weight: 300;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
}

.btn-hero-cta {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 16px 40px;
  background: rgb(118, 172, 175);
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 10px 30px rgba(118, 172, 175, 0.4);
}

.btn-hero-cta:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(118, 172, 175, 0.6);
  background: rgb(98, 152, 155);
  color: white;
}

/* Carousel controls */
.carousel-control-prev,
.carousel-control-next {
  width: 5%;
  opacity: 0.8;
  z-index: 10;
}

.carousel-control-prev:hover,
.carousel-control-next:hover {
  opacity: 1;
}

.carousel-indicators {
  z-index: 10;
}

.carousel-indicators button {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin: 0 6px;
  border: 2px solid white;
  opacity: 0.6;
  transition: all 0.3s;
}

.carousel-indicators button.active {
  opacity: 1;
  transform: scale(1.3);
  background-color: white;
}

/* Carousel fade transitions */
.carousel-fade .carousel-item {
  opacity: 0;
  transition-duration: 0.6s;
  transition-property: opacity;
}

.carousel-fade .carousel-item.active {
  opacity: 1;
}

/* ========== SECTION HEADERS ========== */
.categories-modern,
.products-modern,
.workshops-modern,
.blog-modern {
  padding: 20px 0;
  position: relative;
}

.categories-modern {
  background: #f8fafb;
}

.products-modern {
  background: white;
}

.workshops-modern {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.blog-modern {
  background: white;
}

.section-header {
  text-align: center;
  margin-bottom: 30px;
}

.section-subtitle {
  color: rgb(118, 172, 175);
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  display: block;
  margin-bottom: 10px;
}

.section-title-modern {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2rem, 4vw, 2rem);
  font-weight: 700;
  color: #1a1a1a;
  /*margin-bottom: 20px;*/
}

.title-underline {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, rgb(118, 172, 175), rgb(98, 152, 155));
  margin: 0 auto;
  border-radius: 2px;
}

/* ... All your other existing styles for categories, products, workshops, blog ... */
/* (Keeping them as-is) */

/* ========== CATEGORY CARDS ========== */
.category-card-modern {
  display: block;
  text-decoration: none;
  color: inherit;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.category-card-modern:hover {
  transform: translateY(-10px);
}

.category-image-wrapper {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  margin-bottom: 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}

.category-image {
  width: 100%;
  height: 250px;
  object-fit: cover;
  transition: transform 0.6s ease;
}

.category-card-modern:hover .category-image {
  transform: scale(1.1);
}

.category-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(118,172,175,0.9), rgba(98,152,155,0.9));
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.4s;
}

.category-card-modern:hover .category-overlay {
  opacity: 1;
}

.category-overlay i {
  color: white;
  font-size: 2rem;
  animation: slideRight 0.6s ease;
}

@keyframes slideRight {
  from {
    transform: translateX(-20px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.category-title {
  font-size: 1.2rem;
  text-align: center;
  color: #000;
    font-family: "New York", Iowan Old Style, Apple Garamond, Baskerville, Times New Roman, Droid Serif, Times, Source Serif Pro, serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
}

/* ========== PRODUCT CARDS ========== */
.product-card-modern {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.product-card-modern:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 35px rgba(0,0,0,0.15);
}

.offer-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  z-index: 10;
  background: linear-gradient(135deg, #f93a5a, #f7778c);
  color: white;
  padding: 8px 16px;
  border-radius: 30px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(249, 58, 90, 0.4);
}

.product-image-link {
  display: block;
  text-decoration: none;
}

.product-image-wrapper {
  position: relative;
  overflow: hidden;
  background: #f8f8f8;
}

.product-image {
  width: 100%;
  height: 280px;
  object-fit: cover;
  transition: transform 0.6s ease;
}

.product-card-modern:hover .product-image {
  transform: scale(1.15);
}

.product-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.4s;
}

.product-card-modern:hover .product-overlay {
  opacity: 1;
}

.btn-quick-view {
  background: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

.btn-quick-view:hover {
  transform: scale(1.1);
  background: rgb(118, 172, 175);
  color: white;
}

.product-details {
  padding: 20px;
  text-align: left;
}

.product-name {
  font-size: 1.1rem;
  color: #000;
  margin-bottom: unset;
  line-height: 1.4;
  min-height: 40px;
  font-family: "New York", Iowan Old Style, Apple Garamond, Baskerville, Times New Roman, Droid Serif, Times, Source Serif Pro, serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
}

/* ========== ✨ IMPROVED PRICE BLOCK ========== */
.product-price-block {
  margin-bottom: 15px;
}

/* Shared row layout */
.price-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

/* Regular price (no offer) */
.price-regular {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2d3748;
  letter-spacing: -0.3px;
}

/* Sale current price */
.price-sale-current {
  font-size: 1.3rem;
  font-weight: 800;
  color: #e53e3e;
  letter-spacing: -0.5px;
  line-height: 1;
}

/* "was" label */
.price-was-label {
  font-size: 0.72rem;
  font-weight: 500;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  align-self: flex-end;
  padding-bottom: 2px;
}

/* Original struck-through price */
.price-original-strike {
  font-size: 0.9rem;
  font-weight: 400;
  color: #a0aec0;
  text-decoration: line-through;
  text-decoration-color: #cbd5e0;
  align-self: flex-end;
  padding-bottom: 1px;
}

/* Savings pill */
.price-savings-pill {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #fff5f5, #fed7d7);
  color: #c53030;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 20px;
  border: 1px solid #feb2b2;
  white-space: nowrap;
  letter-spacing: 0.2px;
}

/* Mobile tweaks for price */
@media (max-width: 480px) {
  .price-sale-current {
    font-size: 1.15rem;
  }

  .price-regular {
    font-size: 1.1rem;
  }

  .price-original-strike {
    font-size: 0.82rem;
  }

  .price-savings-pill {
    font-size: 0.62rem;
    padding: 2px 7px;
  }

  .price-was-label {
    font-size: 0.65rem;
  }
}
/* ========== END PRICE BLOCK ========== */

.btn-add-cart {
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-add-cart:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(118, 172, 175, 0.4);
}

/* ========== WORKSHOP CARDS ========== */
.workshop-card-modern {
  background: white;
  border-radius: 20px;
  padding: 30px;
  display: flex;
  gap: 25px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.workshop-card-modern:hover {
  transform: translateX(10px);
  box-shadow: 0 15px 50px rgba(0,0,0,0.2);
}

.workshop-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 8px 20px rgba(118, 172, 175, 0.3);
}

.workshop-icon i {
  color: white;
  font-size: 2rem;
}

.workshop-content {
  flex: 1;
}

.workshop-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.4rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 15px;
}

.workshop-meta {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #4a5568;
  font-size: 0.95rem;
}

.meta-item i {
  color: rgb(118, 172, 175);
  width: 18px;
}

.workshop-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid #e2e8f0;
}

.workshop-price {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
}

.btn-register {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  text-decoration: none;
  border-radius: 30px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s;
}

.btn-register:hover {
  transform: translateX(5px);
  box-shadow: 0 6px 20px rgba(118, 172, 175, 0.4);
  color: white;
}

/* ========== BLOG CARDS ========== */
.blog-card-modern {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.blog-card-modern:hover {
  transform: translateY(-10px) rotate(2deg);
  box-shadow: 0 15px 40px rgba(0,0,0,0.15);
}

.blog-image-wrapper {
  position: relative;
  overflow: hidden;
  height: 250px;
}

.blog-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s;
}

.blog-card-modern:hover .blog-image {
  transform: scale(1.2);
}

.blog-date {
  position: absolute;
  top: 20px;
  right: 20px;
  background: white;
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.date-day {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  line-height: 1;
}

.date-month {
  font-size: 0.75rem;
  color: rgb(118, 172, 175);
  text-transform: uppercase;
  font-weight: 600;
}

.blog-content {
  padding: 25px;
}

.blog-title {
  font-weight: 700;
  font-size: 1.2rem;
  color: #2d3748;
  margin-bottom: 12px;
  line-height: 1.4;
}

.blog-excerpt {
  color: #718096;
  font-size: 0.95rem;
  margin-bottom: 20px;
  line-height: 1.6;
}

.btn-read-more {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgb(118, 172, 175);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s;
}

.btn-read-more:hover {
  color: rgb(98, 152, 155);
  gap: 12px;
}

.btn-view-all {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 35px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.4s;
  box-shadow: 0 8px 20px rgba(118, 172, 175, 0.3);
}

.btn-view-all:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 30px rgba(118, 172, 175, 0.5);
  color: white;
}

/* ========== MOBILE RESPONSIVE ========== */
@media (max-width: 768px) {
  .hero-slide-modern {
    height: auto;
    min-height: unset;
  }
  
  .hero-slide-img {
    position: relative;
    width: 100%;
    height: auto;
    object-fit: contain; /* IMPORTANT */
    padding-top: 91px;
  }
  
  /*.hero-overlay-modern {
    background: linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.35) 100%);
  }*/
  
  .hero-content {
    padding: 0 15px;
    max-width: 90%;
  }
  
  .hero-title {
    font-size: clamp(1.5rem, 5vw, 2rem);
    margin-bottom: 10px;
  }
  
  .hero-description {
    font-size: clamp(0.85rem, 2.5vw, 1rem);
    margin-bottom: 15px;
  }
  
  .btn-hero-cta {
    padding: 10px 24px;
    font-size: 0.9rem;
  }
  
  .carousel-indicators {
    bottom: 15px;
  }
  
  .carousel-indicators button {
    width: 10px;
    height: 10px;
    margin: 0 5px;
  }
  
  .categories-modern,
  .products-modern,
  .workshops-modern,
  .blog-modern {
    padding: 20px 0;
  }


  .workshop-card-modern {
    flex-direction: column;
    text-align: center;
  }

  .workshop-footer {
    flex-direction: column;
    gap: 15px;
  }

  .category-image {
    height: 200px;
  }

  .product-image {
    height: 220px;
  }
}

@media (max-width: 480px) {
  .hero-slide-modern {
    height: 65vh;
    min-height: 450px;
  }
  
  .hero-title {
    font-size: clamp(1.25rem, 6vw, 1.75rem);
  }
  
  .hero-description {
    font-size: 0.8rem;
  }
  
  .btn-hero-cta {
    padding: 8px 20px;
    font-size: 0.85rem;
  }
}

@media (max-width: 375px) {
  .hero-slide-modern {
    height: 60vh;
    min-height: 420px;
  }
}

/* ========== REGISTRATION MODAL ========== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  backdrop-filter: blur(5px);
}

.modal-content {
  background: white;
  border-radius: 20px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0,0,0,0.1);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 1;
}

.modal-close:hover {
  background: rgba(0,0,0,0.2);
  transform: rotate(90deg);
}

.modal-header {
  padding: 40px 40px 20px 40px;
  border-bottom: 2px solid #e2e8f0;
}

.modal-header h3 {
  font-family: 'Playfair Display', serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 5px;
}

.registration-form {
  padding: 30px 40px 40px 40px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
  font-size: 0.95rem;
}

.form-control {
  width: 100%;
  padding: 12px 18px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s;
  font-family: 'Inter', sans-serif;
}

.form-control:focus {
  outline: none;
  border-color: rgb(118, 172, 175);
  box-shadow: 0 0 0 3px rgba(118, 172, 175, 0.1);
}

textarea.form-control {
  resize: vertical;
  min-height: 100px;
}

.modal-footer-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 2px solid #e2e8f0;
  margin-top: 20px;
}

.total-price {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.total-price span {
  color: #718096;
  font-size: 0.9rem;
}

.total-price strong {
  font-size: 1.8rem;
  color: #2d3748;
}

.btn-submit {
  padding: 14px 32px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(118, 172, 175, 0.4);
}

.btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

@media (max-width: 768px) {
  .modal-content {
    border-radius: 20px 20px 0 0;
    max-height: 95vh;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    max-width: 100%;
  }

  .modal-header,
  .registration-form {
    padding: 25px 20px;
  }

  .modal-footer-custom {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .btn-submit {
    width: 100%;
    justify-content: center;
  }
}

.workshop-cta-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
}

.btn-register-whatsapp {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #25D366;
  color: white;
  text-decoration: none;
  border-radius: 30px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s;
}

.btn-register-whatsapp:hover {
  background: #1ebe57;
  transform: translateX(5px);
  box-shadow: 0 6px 20px rgba(37, 211, 102, 0.35);
  color: white;
}

@media (max-width: 768px) {
  .workshop-cta-buttons {
    width: 100%;
    align-items: stretch;
  }
  .btn-register-whatsapp {
    justify-content: center;
  }
}
</style>